import { getAiApiUrl } from '../../config';
import { storage } from '../../lib/storage';
import { notify } from '../../utils/toast';
import { Conversation } from '../../types/chat';

interface ConversationApiResponse {
  limit: number;
  has_more: boolean;
  data: Array<{
    id: string;
    name: string;
    inputs: any;
    status: string;
    created_at: number;
    updated_at: number;
  }>;
}

/**
 * Fetches conversations from the API and updates local storage
 * @param auth Authentication data
 * @param limit Optional limit for number of conversations to fetch
 * @param lastId Optional ID of the last conversation for pagination
 * @returns Array of conversations
 */
export const fetchConversations = async (
  auth: { jwt: string; walletAddress: string; nonce: string; did: string },
  limit?: number,
  lastId?: string
): Promise<Conversation[]> => {
  const loadingToastId = notify.loading('Fetching conversations...');

  try {
    // Fetch conversations from API
    const response = await fetch(`${getAiApiUrl()}/conversations?user=${auth.did.toLowerCase().replaceAll('_uuid', ':uuid').replaceAll('_addr', ':addr').replaceAll('_asa', ':asa').replaceAll('_app', ':app').replaceAll('_txn', ':txn')}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth.jwt}`
      },
      body: JSON.stringify({
        walletAddress: auth.walletAddress,
        nonce: auth.nonce,
        limit: 50,
        last_id: lastId
      })
    });

    if (!response.ok) {
      let errorMessage = await response.text()
      if (errorMessage.indexOf('Insufficient') !== -1) {
        notify.error('Insufficient balance to fetch conversations. Please top up your account.');
      }
      console.error('Failed to fetch conversations:', errorMessage);
      throw new Error('Failed to fetch conversations');
    }

    const apiResponse: ConversationApiResponse = await response.json();

    // Convert API response to local conversation format
    const conversations: Conversation[] = apiResponse.data.map(conv => ({
      id: conv.id,
      title: conv.name,
      created_at: new Date(conv.created_at * 1000).toISOString(),
      updated_at: new Date(conv.updated_at * 1000).toISOString(),
      status: conv.status,
      isPinned: false, // Default to false as API doesn't store pin status
      isNew: false
    }));

    // Get existing conversations from local storage
    if (!auth.walletAddress) {
      throw new Error('Wallet address is required');
    }

    // Store wallet address in a variable for use throughout the function
    const walletAddress = auth.walletAddress;

    const localConversations = await storage.getConversations(walletAddress);

    // Create a map of local conversations by ID for quick lookup
    const localConversationMap = new Map<string, Conversation>();
    localConversations.forEach(conv => {
      localConversationMap.set(conv.id, conv);
    });

    // Create a set of API conversation IDs for quick lookup
    const apiConversationIds = new Set(conversations.map(conv => conv.id));

    // Delete local conversations that don't exist in the API response
    for (const localConv of localConversations) {
      if (!apiConversationIds.has(localConv.id) && !localConv.isNew) {
        console.log(`Deleting local conversation that doesn't exist upstream: ${localConv.id}`);
        await storage.removeConversation(walletAddress, localConv.id);
      }
    }

    // Update local storage with API conversations, preserving local-only data like isPinned
    for (const apiConv of conversations) {
      const localConv = localConversationMap.get(apiConv.id);

      // If conversation exists locally, preserve isPinned status
      if (localConv) {
        apiConv.isPinned = localConv.isPinned;

        // Update the conversation in local storage
        await storage.updateConversation(walletAddress, apiConv.id, apiConv);
      } else {
        // Create new conversation in local storage
        await storage.setConversation(walletAddress, apiConv);
      }
    }

    notify.dismiss(loadingToastId || '');
    if (!window.location.pathname.startsWith('/convo/shared')) {
      notify.success('Conversations synchronized');
    }


    // Get updated conversations from local storage after all changes
    const updatedLocalConversations = await storage.getConversations(walletAddress);
    return updatedLocalConversations;
  } catch (error: any) {
    console.error('Error fetching conversations:', error);
    notify.dismiss(loadingToastId || '');
    notify.error(error.message || 'Failed to fetch conversations');

    // Return local conversations as fallback
    if (!auth.walletAddress) {
      return [];
    }
    // We've checked that walletAddress is not null above
    return storage.getConversations(auth.walletAddress);
  }
};
